
import React, { useContext, useState, useEffect } from 'react';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { Link, useNavigate } from 'react-router-dom';
import {
  Col,
  Row,
  Input,
  Button,
  Spinner,
  FormGroup,
  InputGroup,
  InputGroupText,
} from 'reactstrap';
import { authAxios } from 'APIs';
import { LOGIN_MODEL } from './constant';
import { AuthContext, AuthContextType } from 'hooks/useAuth';
import { encryptPassword, decryptPassword } from 'utils/rememberMe';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { setAuthData } = useContext(AuthContext) as AuthContextType;

  const [loginCreds, setLoginCreds] = useState(LOGIN_MODEL);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({
    email: false,
    password: false,
  });

  const storedAccessToken = localStorage.getItem('accessToken');

  useEffect(() => {
    if (storedAccessToken) {
      if (storedAccessToken !== 'null' && window.location.pathname === '/') {
        navigate('/app/dashboard', { state: { heading: 'Dashboard' } });
      }
    } else {
      navigate('/');
    }
    const savedEmail = localStorage.getItem('email');
    const savedRememberMe = localStorage.getItem('rememberMe') === 'true';

    if (savedRememberMe && savedEmail) {
      setRememberMe(true);
      setLoginCreds((prev) => ({ ...prev, email: savedEmail }));
    }

    const encryptedPassword = localStorage.getItem('encryptedPassword');
    const iv = localStorage.getItem('iv');

    if (savedRememberMe && savedEmail && encryptedPassword && iv) {
      (async () => {
        const authToken: any = localStorage.getItem('authToken');

        try {
          const key = await crypto.subtle.importKey(
            'jwk',
            JSON.parse(authToken),
            { name: 'AES-GCM' },
            false,
            ['decrypt']
          );

          const decryptedPassword = await decryptPassword(
            encryptedPassword,
            iv,
            key
          );
          setLoginCreds((prev) => ({ ...prev, password: decryptedPassword }));
        } catch (error) {
          console.error('Error during decryption:', error);
        }
      })();
    } else {
      setLoginCreds((prev) => ({ ...prev, password: '' }));
    }
  }, [navigate,storedAccessToken]);

  const validationSchema = yup.object({
    email: yup
      .string()
      .email('Invalid email format')
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Invalid email format')
      .required('Email is required'),
    password: yup
      .string()
      .min(6, 'Password must be at least 6 characters long')
      .required('Password is required'),
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setLoginCreds((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) {
      // Revalidate the field when it changes
      validationSchema
        .validateAt(name, { [name]: value })
        .then(() => {
          setErrors((prev) => ({ ...prev, [name]: '' }));
        })
        .catch((err) => {
          setErrors((prev) => ({ ...prev, [name]: err.message }));
        });
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    // Validate the field on blur
    validationSchema
      .validateAt(name, { [name]: loginCreds[name as keyof typeof loginCreds] })
      .then(() => {
        setErrors((prev) => ({ ...prev, [name]: '' }));
      })
      .catch((err) => {
        setErrors((prev) => ({ ...prev, [name]: err.message }));
      });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setLoginCreds((prev) => ({ ...prev, isSubmitting: true }));

    // let loginResponse = { ...auth };

    try {
      await validationSchema.validate(loginCreds, { abortEarly: false });

      const {
        data: { data },
        status,
      } = await authAxios.post('/login', {
        email: loginCreds.email,
        password: loginCreds.password,
      });

      if (status === 200) {
        console.log('Login API response:', data);

        // ✅ SET AUTH CONTEXT (single source of truth)
        setAuthData({
          loading: false,
          data: {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            refreshTokenExpiration: data.refreshTokenExpiration,
            user: data.user,
          },
        });

        // ✅ LOCAL STORAGE (persistent auth)
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        localStorage.setItem(
          'refreshTokenExpiration',
          String(data.refreshTokenExpiration)
        );
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('test', "hitu");
        localStorage.setItem('ankit', JSON.stringify(data.user));
        console.log("'User data stored in localStorage:", data.user);

        // ✅ REMEMBER ME
        if (rememberMe) {
          localStorage.setItem('email', loginCreds.email);
          localStorage.setItem('rememberMe', 'true');
          encryptPassword(loginCreds.password);
        } else {
          localStorage.removeItem('email');
          localStorage.removeItem('encryptedPassword');
          localStorage.removeItem('iv');
          localStorage.removeItem('authToken');
          localStorage.removeItem('rememberMe');
        }

        toast.success('Login successful!');
        navigate('/app/dashboard', { state: { heading: 'Dashboard' } });
      }
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const fieldErrors: Record<string, string> = {};
        error.inner.forEach((err) => {
          if (err.path) fieldErrors[err.path] = err.message;
        });
        setErrors(fieldErrors);
      } else {
        toast.error('Login failed. Please check credentials.');
      }
    } finally {
      // ✅ IMPORTANT FIX
      setLoginCreds((prev) => ({ ...prev, isSubmitting: false }));
    }
  };

  const handleRememberMeChange = (checked: boolean) => {
    setRememberMe(checked);

    if (checked) {
      localStorage.setItem('rememberMe', 'true');
    } else {
      localStorage.removeItem('rememberMe');
      localStorage.removeItem('email');
      localStorage.removeItem('encryptedPassword');
      localStorage.removeItem('iv');
      localStorage.removeItem('authToken');
    }
  };

  const { isSubmitting } = loginCreds;
  // const { isSubmitting } = loginCreds;
  return (
    <Row className="getway-wrapper login-page">
      <Col
        sm="4"
        md="6"
        lg="6"
        className="d-flex justify-content-center align-items-center login-image-container"
      >
        <img
          src={require('images/dyo-logo1.png')}
          alt="Login"
          className="login-image img-fluid"
        />
      </Col>
      <Col sm="8" md="6" lg="6" className="bg-light-gray login-form-container">
        <form onSubmit={handleSubmit}>
          <div className="login-form-box p-4">
            <Row>
              <Col sm="12">
                <h2 className="text-center mb-4">
                  Welcome to <span className="title">DYO CHEF</span>
                </h2>
              </Col>
              <Col sm="12" className="email-field-container">
                <FormGroup className="d-flex flex-column">
                  <label className="form-label">Email</label>

                  <Input
                    type="email"
                    name="email"
                    value={loginCreds.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`form-control ${
                      touched.email && errors.email ? 'is-invalid' : ''
                    }`}
                    placeholder="Enter your Email address"
                  />
                  {errors.email && (
                    <small className="text-danger mb-0">{errors.email}</small>
                  )}
                </FormGroup>
              </Col>
              <Col sm="12" className="email-field-container">
                <FormGroup className="d-flex flex-column">
                  <label className="form-password-label mb-2">Password</label>

                  <InputGroup className="input-box">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={loginCreds.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`form-control ${
                        touched.password && errors.password ? 'is-invalid' : ''
                      }`}
                      placeholder="Enter your Password"
                    />

                    <InputGroupText>
                      <button
                        className="password-button"
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={
                          showPassword ? 'Show password' : 'Hide password'
                        }
                      >
                        {showPassword ? (
                          <i className="bi bi-eye-slash"></i>
                        ) : (
                          <i className="bi bi-eye"></i>
                        )}
                      </button>
                    </InputGroupText>
                  </InputGroup>
                  {errors.password && (
                    <small className="text-danger">{errors.password}</small>
                  )}
                </FormGroup>
              </Col>
              <Col sm="12">
                <FormGroup className="d-flex flex-column">
                  <div className="align-items-center gap-4">
                    <Col xs="auto">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="rememberMe"
                        checked={rememberMe}
                        onChange={(e) =>
                          handleRememberMeChange(e.target.checked)
                        }
                      />
                      <label
                        className="form-check-label ms-2"
                        htmlFor="rememberMe"
                      >
                        Remember Me
                      </label>
                    </Col>
                  </div>
                </FormGroup>
              </Col>
              <Col sm="12">
                <Button
                  type="submit"
                  className="button login-button w-100"
                  disabled={
                    !validationSchema.isValidSync(loginCreds) || isSubmitting
                  }
                >
                  {isSubmitting ? <Spinner size="sm" /> : null}
                  Log In
                </Button>
              </Col>

              <Col className="text-end">
                <p className="mb-0 already-a-member">
                  <Link to="/forgot-password">Forgot Password?</Link>
                </p>
              </Col>
            </Row>
          </div>
        </form>
      </Col>
    </Row>
  );
};

export default Login;
